import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAvailabilityStatus } from "../../api/availability";
import { createExternalAccounts } from "../../api/finalDelivery";
import { formatDate } from "../../utils/getEntitiesUtils";
import AvailabilityRequestDialog from "./AvailabilityRequestDialog";

const roleLabel = (teacher) => {
  const parts = [];
  if (teacher.directed_groups > 0) {
    parts.push(
      teacher.directed_groups === 1
        ? "Director/a (1 equipo)"
        : `Director/a (${teacher.directed_groups} equipos)`
    );
  }
  if (teacher.evaluated_groups > 0) {
    parts.push(
      teacher.evaluated_groups === 1
        ? "Evaluador/a (1 equipo)"
        : `Evaluador/a (${teacher.evaluated_groups} equipos)`
    );
  } else if (teacher.is_evaluator) {
    parts.push("Evaluador/a");
  }
  return parts.join(" · ");
};

/**
 * Disponibilidad de directores y evaluadores, en la pantalla del algoritmo de
 * fechas. Lista solo a quienes la tienen que cargar (directores de equipos sin
 * fecha y evaluadores), que es la misma regla con la que el backend frena el
 * algoritmo, y permite pedírsela por mail a los que faltan.
 */
const AvailabilityPanel = () => {
  const user = useSelector((state) => state.user);
  const period = useSelector((state) => state.period);
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creatingAccounts, setCreatingAccounts] = useState(false);
  const [accountsResult, setAccountsResult] = useState(null);

  const handleCreateAccounts = async () => {
    setCreatingAccounts(true);
    try {
      setAccountsResult(await createExternalAccounts(user, period.id));
      await load();
    } catch (e) {
      setAccountsResult({ error: e?.response?.data?.detail || "No se pudieron crear las cuentas." });
    } finally {
      setCreatingAccounts(false);
    }
  };

  const load = useCallback(async () => {
    if (!period?.id) return;
    try {
      setError(null);
      setStatus(await getAvailabilityStatus(user, period.id));
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo cargar la disponibilidad de los docentes.");
    }
  }, [user, period]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <Grid item xs={12}>
        <Alert severity="error">{error}</Alert>
      </Grid>
    );
  }
  if (!status) {
    return (
      <Grid item xs={12}>
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      </Grid>
    );
  }

  const missing = status.teachers.filter((t) => !t.loaded);

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="h6">Disponibilidad de directores y evaluadores</Typography>
            <Typography variant="body2" color="text.secondary">
              {status.teachers.length === 0
                ? "No hay equipos sin fecha ni evaluadores en este cuatrimestre."
                : missing.length === 0
                ? `Los ${status.teachers.length} cargaron su disponibilidad.`
                : `Faltan ${missing.length} de ${status.teachers.length}. Mientras falte alguien, el algoritmo no corre salvo que lo fuerces.`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<MailOutlineIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={missing.length === 0 || !status.enabled}
          >
            Pedir disponibilidad
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          {status.mode === "asignados"
            ? "Evaluadores: los asignados a cada equipo (los mismos de la entrega final). El algoritmo busca un horario en el que puedan el equipo, su director y todos sus evaluadores."
            : "Evaluadores: los marcados en \"Seleccionar evaluadores\". El algoritmo elige uno por equipo."}
        </Typography>

        {accountsResult && (
          <Alert
            severity={accountsResult.error || accountsResult.skipped?.length ? "warning" : "success"}
            sx={{ mb: 1 }}
            onClose={() => setAccountsResult(null)}
          >
            {accountsResult.error ||
              `Se crearon ${accountsResult.created.length} cuentas y se actualizaron ${accountsResult.linked} evaluadores.` +
                (accountsResult.skipped?.length
                  ? ` No se pudo con: ${accountsResult.skipped.map((s) => `${s.email} (${s.reason})`).join(", ")}.`
                  : "")}
          </Alert>
        )}

        {status.external_evaluators?.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 1 }}
            action={
              <Button color="inherit" size="small" onClick={handleCreateAccounts} disabled={creatingAccounts}>
                {creatingAccounts ? <CircularProgress size={16} color="inherit" /> : "Crearles cuenta"}
              </Button>
            }
          >
            Hay evaluadores sin cuenta en el sistema: no pueden cargar disponibilidad y el
            algoritmo no los tiene en cuenta:{" "}
            {status.external_evaluators
              .map((e) => `${e.name}${e.email ? ` <${e.email}>` : ""} (${e.groups} ${e.groups === 1 ? "equipo" : "equipos"})`)
              .join(", ")}
            . "Crearles cuenta" les crea un usuario de tutor con contraseña aleatoria; entran con
            "¿Olvidaste tu contraseña?" (el pedido de disponibilidad ya se los explica).
          </Alert>
        )}

        {!status.enabled && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            La carga de disponibilidad está cerrada: los docentes no pueden cargar nada hasta que
            la habilites en la configuración del cuatrimestre.
          </Alert>
        )}

        {status.teachers.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Docente</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Disponibilidad</TableCell>
                <TableCell>Último pedido</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status.teachers.map((t) => (
                <TableRow key={t.user_id}>
                  <TableCell>
                    {t.name}
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t.email}
                    </Typography>
                  </TableCell>
                  <TableCell>{roleLabel(t)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={t.loaded ? "success" : "warning"}
                      label={t.loaded ? `Cargó (${t.slots} horarios)` : "Falta"}
                    />
                    {!t.has_period && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        No es tutor/a de este cuatrimestre: se le da acceso al pedirle la
                        disponibilidad.
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{t.last_request_at ? formatDate(t.last_request_at) : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <AvailabilityRequestDialog
        open={dialogOpen}
        period={period}
        onClose={() => setDialogOpen(false)}
        onSent={(data) => setStatus((prev) => ({ ...prev, teachers: data.teachers }))}
        onEditTemplate={() => navigate("/plantilla-pedido-disponibilidad")}
      />
    </Grid>
  );
};

export default AvailabilityPanel;
